
import React from 'react';
import TodayLessons from '../TodayLessons';
import PerformanceBadge from '../PerformanceBadge';
import StudentCard from './StudentCard';
import SkillTree from './SkillTree/SkillTree';
import ChatRoom from './Chat/ChatRoom';
import CoachSharedPersonalNotesSection from './CoachSharedPersonalNotesSection';
import CoachSpanishPanel from './Spanish/CoachSpanishPanel';
import CoachUpdatesOverviewTab from './Bookings/CoachUpdatesOverviewTab';
import MyAvailableSlotsTab from './Bookings/MyAvailableSlotsTab';
import BookingRequestsTab from './Bookings/BookingRequestsTab';
import TutorialsTab from './Tutorials/TutorialsTab';
import LessonTypeWaitlistTab from './Bookings/LessonTypeWaitlistTab';
import CoachStudentSharingTab from './Sharing/CoachStudentSharingTab';
import PadelNewsTab from './News/PadelNewsTab';
import PhotoUploadWidget from './PhotoUploadWidget';
import AttachmentGallery from './AttachmentGallery';
import LoadingSkeleton from './LoadingSkeleton';
import RichTextDisplay from './RichTextDisplay';
import RichTextEditor from './RichTextEditor';
import FormattedContent from './FormattedContent';
import ThemeSkillChips from './SkillTree/ThemeSkillChips';
import ThemeLabelPicker from './ThemeLabelPicker';
import { notes, pocket } from '../api';
import { pocketEnabledFor } from '../utils/features';
import { getPlainText } from '../utils/htmlText';
import '../styles/noteUrls.css';
import { peekNavTarget, takeNavTarget, onNavTarget } from '../notificationNavigation';

// Custom hook to detect if tab text should be shown (responsive)
const useShowTabText = () => {
  const [showText, setShowText] = React.useState(window.innerWidth > 480);

  React.useEffect(() => {
    const handleResize = () => {
      setShowText(window.innerWidth > 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return showText;
};

function CoachView({
  // User data
  user,

  // State
  viewingStudent,
  setViewingStudent,
  players,
  setPlayers,
  filteredPlayers,
  setFilteredPlayers,
  searchQuery,
  setSearchQuery,
  showAddStudent,
  setShowAddStudent,
  newStudentEmail,
  setNewStudentEmail,
  searchSuggestions,
  setSearchSuggestions,
  showSuggestions,
  setShowSuggestions,
  refreshing,
  setRefreshing,
  showModal,
  setShowModal,
  selectedLesson,
  setSelectedLesson,
  lessonNote,
  setLessonNote,
  lessonNotes,
  modalConfig,
  setModalConfig,
  refreshCalendarRef,
  scrollKey,
  setScrollKey,
  editingNoteId,
  setEditingNoteId,
  editNoteText,
  setEditNoteText,
  newNote,
  setNewNote,
  newNoteBadge,
  setNewNoteBadge,
  lessonNotePhotos,
  setLessonNotePhotos,
  newNoteType,
  setNewNoteType,

  // Loading states
  loadingPlayers = false,

  // Functions from App.js
  handleLogout,
  loadPlayers,
  syncBufferLessons,
  loadLessonNotes,
  handleBackToList,
  handleViewStudent,
  handleRemoveStudent,
  handleAddStudent,
  handleSearchPlayers,
  handleSelectSuggestion,
  handleLessonClick,
  handleSaveLessonNote,
  handleStartEdit,
  handleCancelEdit,
  closeModal,
  showAlertModal,
  formatDateTime,
  refreshPlayersAfterDrop
}) {
  const [studentViewTab, setStudentViewTab] = React.useState('notes');
  const [longTapStudentId, setLongTapStudentId] = React.useState(null);
  const [longTapStudent, setLongTapStudent] = React.useState(null);
  const [showRoleMenu, setShowRoleMenu] = React.useState(false);
  const [isChangingRole, setIsChangingRole] = React.useState(false);
  const [coachMainTab, setCoachMainTab] = React.useState(() => { // 'students', 'questions', 'bookings', 'tutorials', 'type-waitlist', 'partnerships', 'padel-matches', 'updates'
    // After a pull-to-refresh this screen is rebuilt; come back to the same section instead of Students
    try {
      if (sessionStorage.getItem('cpResumeCoachTab')) {
        sessionStorage.removeItem('cpResumeCoachTab');
        return sessionStorage.getItem('cpCoachMainTab') || 'students';
      }
    } catch (e) { /* ignore */ }
    return 'students';
  });
  React.useEffect(() => { try { sessionStorage.setItem('cpCoachMainTab', coachMainTab); } catch (e) { /* ignore */ } }, [coachMainTab]);
  const [slotsRefreshKey, setSlotsRefreshKey] = React.useState(0);
  const [notePhotos, setNotePhotos] = React.useState([]); // For regular note photos

  // 🎙️ Pocket notes for viewed student
  const [pocketNotes, setPocketNotes] = React.useState([]);
  const [pocketNotesLoading, setPocketNotesLoading] = React.useState(false);

  // Tabs scroll reference
  const tabsScrollRef = React.useRef(null);

  // Content scroll references for scroll-to-top on tab change
  const coachMainContentRef = React.useRef(null);
  const studentViewContentRef = React.useRef(null);

  // Check if tab text should be shown (responsive)
  const showTabText = useShowTabText();

  // Scroll to top when coach main tab changes
  React.useEffect(() => {
    if (coachMainContentRef.current) {
      coachMainContentRef.current.scrollTop = 0;
    } else {
      // Fallback for when content is not in a ref (scrolling handled by parent)
      window.scrollTo(0, 0);
    }
  }, [coachMainTab]);

  // Scroll to top when student view tab changes or when opening a student
  React.useEffect(() => {
    // Use setTimeout to ensure scroll happens after render
    const timeoutId = setTimeout(() => {
      if (studentViewContentRef.current) {
        studentViewContentRef.current.scrollTop = 0;
      }
      // Also try by ID as fallback
      const element = document.getElementById('student-notes-scroll-container');
      if (element) {
        element.scrollTop = 0;
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [studentViewTab, viewingStudent]);

  // Notification tap: switch to the right main tab and hand the section /
  // student to the child that shows it. (Account-level targets like Settings
  // are App's job, so those are left in place.)
  const [navTarget, setNavTarget] = React.useState(null);
  React.useEffect(() => {
    const apply = () => {
      const pending = peekNavTarget();
      if (!pending || pending.role !== 'coach' || pending.appTab) return;
      const target = takeNavTarget('coach');
      if (!target) return;

      if (target.tab) setCoachMainTab(target.tab);
      setNavTarget(target);

      // Open the student a notification is about (App resolves it once the
      // roster is loaded); the effect below then picks the sub-tab / note.
      if (target.tab === 'students' && target.playerId) {
        sessionStorage.setItem('focusStudentNote', JSON.stringify({
          playerId: target.playerId,
          noteId: target.noteId || null,
          studentTab: target.studentTab || 'notes',
          logId: target.logId || null
        }));
        window.dispatchEvent(new Event('focusStudentNote'));
      } else if (target.tab === 'students') {
        setViewingStudent(null); // back to the student list
      }
    };
    apply();
    return onNavTarget(apply);
  }, []);

  // Bumped on every briefing tap, so tapping again (or tapping while that very
  // student is already open) still re-runs the scroll-to-note below.
  const [focusTick, setFocusTick] = React.useState(0);
  React.useEffect(() => {
    const bump = () => setFocusTick(t => t + 1);
    window.addEventListener('focusStudentNote', bump);
    return () => window.removeEventListener('focusStudentNote', bump);
  }, []);

  // Lesson briefing notification tap: switch to the Notes tab, scroll to the
  // specific note and flash it. Notes render after loading, so retry briefly.
  React.useEffect(() => {
    let raw;
    try { raw = sessionStorage.getItem('focusStudentNote'); } catch (e) { return undefined; }
    if (!raw || !viewingStudent?._id) return undefined;

    let ctx;
    try { ctx = JSON.parse(raw); } catch (e) { return undefined; }
    if (ctx.playerId !== viewingStudent._id) return undefined;

    setCoachMainTab('students');
    setStudentViewTab(ctx.studentTab && ctx.studentTab !== 'personal' ? ctx.studentTab : 'notes');

    if (!ctx.noteId) {
      sessionStorage.removeItem('focusStudentNote');
      return undefined;
    }

    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const el = ctx.noteId ? document.querySelector(`[data-note-id="${ctx.noteId}"]`) : null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('note-focus-flash');
        setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
        sessionStorage.removeItem('focusStudentNote');
        clearInterval(timer);
      } else if (tries >= 20) {
        // Note not found (deleted / different list) - the student's notes are open anyway
        sessionStorage.removeItem('focusStudentNote');
        clearInterval(timer);
      }
    }, 300);
    return () => clearInterval(timer);
  }, [viewingStudent?._id, focusTick]);

  // 🎙️ Load Pocket notes when viewing a student
  React.useEffect(() => {
    if (pocketEnabledFor(user?.role) && viewingStudent?._id && studentViewTab === 'notes') {
      loadPocketNotesForStudent();
    }
  }, [viewingStudent?._id, studentViewTab]);

  const loadPocketNotesForStudent = async () => {
    setPocketNotesLoading(true);
    try {
      const response = await pocket.getPlayerNotes(viewingStudent._id, user?._id);
      setPocketNotes(response.data.playerNotes || []);
    } catch (err) {
      console.error('❌ Error loading Pocket notes:', err);
      setPocketNotes([]);
    } finally {
      setPocketNotesLoading(false);
    }
  };

  const handleLabelNoteTheme = async (noteId, themeName) => {
    await notes.labelNoteTheme(viewingStudent._id, noteId, themeName);
    const response = await notes.getAllPlayers();
    const freshStudent = response.data.find(p => p._id === viewingStudent._id);
    if (freshStudent) {
      setViewingStudent(freshStudent);
    }
  };

  // Reset photo state when switching students
  React.useEffect(() => {
    setNotePhotos([]);
  }, [viewingStudent]);

  // Handle long-tap on student card
  const handleStudentLongTap = (studentId, student) => {
    setLongTapStudentId(studentId);
    setLongTapStudent(student);
    setShowRoleMenu(true);
  };

  // Promote student to manager
  const handlePromoteToManager = async () => {
    if (!longTapStudentId) return;

    setIsChangingRole(true);
    try {
      const response = await notes.updateStudentRole(longTapStudentId, 'manager');
      showAlertModal('Success', `${response.data.student.name} is now a Manager! 👑`);

      // Update local players list
      setFilteredPlayers(filteredPlayers.map(p =>
        p._id === longTapStudentId ? { ...p, role: 'manager' } : p
      ));
      setPlayers(players.map(p =>
        p._id === longTapStudentId ? { ...p, role: 'manager' } : p
      ));

      // Close popup
      setShowRoleMenu(false);
      setLongTapStudentId(null);
      setLongTapStudent(null);
    } catch (error) {
      showAlertModal('Error', error.response?.data?.error || 'Failed to promote student');
    } finally {
      setIsChangingRole(false);
    }
  };

  // Demote manager back to player
  const handleDemoteFromManager = async () => {
    if (!longTapStudentId) return;

    // Optional confirmation
    const confirmed = window.confirm('Remove manager privileges from this student?');
    if (!confirmed) return;

    setIsChangingRole(true);
    try {
      const response = await notes.updateStudentRole(longTapStudentId, 'player');
      showAlertModal('Success', `${response.data.student.name} is now a Player`);

      // Update local players list
      setFilteredPlayers(filteredPlayers.map(p =>
        p._id === longTapStudentId ? { ...p, role: 'player' } : p
      ));
      setPlayers(players.map(p =>
        p._id === longTapStudentId ? { ...p, role: 'player' } : p
      ));

      // Close popup
      setShowRoleMenu(false);
      setLongTapStudentId(null);
      setLongTapStudent(null);
    } catch (error) {
      showAlertModal('Error', error.response?.data?.error || 'Failed to change student role');
    } finally {
      setIsChangingRole(false);
    }
  };

  // ═══════════════════════════════════════════════════════
  // FULLSCREEN STUDENT DETAIL VIEW - NO HEADER (App.js handles it)
  // ═══════════════════════════════════════════════════════
  if (viewingStudent) {
    return (
      <>
        {/* Scrollable Content Area */}
        <div
          ref={studentViewContentRef}
          key={scrollKey}
          id="student-notes-scroll-container"
          style={{
            height: '100%',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            background: '#f5f5f5'
          }}
        >
          <div style={{
            padding: '20px',
            paddingBottom: '100px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Back to List Button */}
            <button
              onClick={handleBackToList}
              style={{
                marginBottom: '20px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(47, 75, 162, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(47, 75, 162, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(47, 75, 162, 0.3)';
              }}
            >
              <span style={{ fontSize: '16px' }}>←</span>
              <span>Back to Students</span>
            </button>

            {/* Student Name Header */}
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: '24px',
              fontWeight: '700',
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '32px' }}>👤</span>
              {viewingStudent.name}
            </h2>

            {/* Notes/Skills/Personal Notes Toggle */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setStudentViewTab('notes')}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '12px 16px',
                  background: studentViewTab === 'notes' ? '#2f4ba2' : '#ecf0f1',
                  color: studentViewTab === 'notes' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                📝 Notes
              </button>
              <button
                onClick={() => setStudentViewTab('skills')}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '12px 16px',
                  background: studentViewTab === 'skills' ? '#2f4ba2' : '#ecf0f1',
                  color: studentViewTab === 'skills' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                ⭐ Skills
              </button>
              <button
                onClick={() => setStudentViewTab('spanish')}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '12px 16px',
                  background: studentViewTab === 'spanish' ? '#2f4ba2' : '#ecf0f1',
                  color: studentViewTab === 'spanish' ? 'white' : '#2c3e50',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                🇪🇸 Spanish
              </button>
            </div>

            {/* Add Note Section - Enhanced with Badge & Tag */}
            {studentViewTab === 'notes' && (
              <>
              <div style={{
              marginBottom: '30px',
              // Same literal values as the note cards below (white / #2c3e50)
              // so this panel has a light background in light theme, and the
              // global dark-theme sweep in darkTheme.css re-colors it for
              // dark theme the same way.
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              border: '2px solid rgba(47, 75, 162, 0.2)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  boxShadow: '0 4px 12px rgba(47, 75, 162, 0.3)'
                }}>
                  ✍️
                </div>
                <h3 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#2c3e50'
                }}>
                  Add New Note
                </h3>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <RichTextEditor
                  value={newNote}
                  onChange={setNewNote}
                  maxCharacters={2000}
                  placeholder={`Write a note for ${viewingStudent.name}...`}
                />
              </div>

              {/* Performance Badge Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#2c3e50'
                }}>
                  Performance Badge (Optional)
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {[
                    { value: null, label: 'None', color: '#95a5a6' },
                    { value: 'excellent', label: '⭐ Excellent', color: '#FFD700' },
                    { value: 'good', label: '👍 Good', color: '#27ae60' },
                    { value: 'average', label: '👌 Average', color: '#3498db' },
                    { value: 'needs_work', label: '📈 Needs Work', color: '#e67e22' },
                    { value: 'keep_trying', label: '💪 Keep Trying', color: '#9b59b6' }
                  ].map(badge => (
                    <button
                      key={badge.value || 'none'}
                      onClick={() => setNewNoteBadge(badge.value)}
                      style={{
                        padding: '8px 16px',
                        background: newNoteBadge === badge.value ? badge.color : '#f8f9fa',
                        color: newNoteBadge === badge.value ? 'white' : '#2c3e50',
                        border: `2px solid ${badge.color}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (newNoteBadge !== badge.value) {
                          e.target.style.background = badge.color;
                          e.target.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (newNoteBadge !== badge.value) {
                          e.target.style.background = '#f8f9fa';
                          e.target.style.color = '#2c3e50';
                        }
                      }}
                    >
                      {badge.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Note Type Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#2c3e50'
                }}>
                  Note Type (Optional)
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {[
                    { value: null, label: 'None', color: '#95a5a6' },
                    { value: 'lesson', label: '📚 Lesson', color: '#3498db' },
                    { value: 'wisdom', label: '💡 Wisdom', color: '#f39c12' }
                  ].map(type => (
                    <button
                      key={type.value || 'none'}
                      onClick={() => setNewNoteType(type.value)}
                      style={{
                        padding: '8px 16px',
                        background: newNoteType === type.value ? type.color : '#f8f9fa',
                        color: newNoteType === type.value ? 'white' : '#2c3e50',
                        border: `2px solid ${type.color}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (newNoteType !== type.value) {
                          e.target.style.background = type.color;
                          e.target.style.color = 'white';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (newNoteType !== type.value) {
                          e.target.style.background = '#f8f9fa';
                          e.target.style.color = '#2c3e50';
                        }
                      }}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo Upload Widget for Regular Notes */}
              <div style={{ marginBottom: '20px' }}>
                <PhotoUploadWidget
                  key={viewingStudent?._id || 'new'}
                  existingPhotos={notePhotos}
                  maxPhotos={10}
                  onPhotosAdded={(photos) => {
                    setNotePhotos(photos);
                  }}
                  onPhotoRemoved={(photoId) => {
                    setNotePhotos(prev => prev.filter(p => p.id !== photoId));
                  }}
                />
              </div>

              <button
                onClick={async () => {
                  if (!getPlainText(newNote)) return;

                  try {
                    // Save the note first
                    const savedNote = await notes.addNote(viewingStudent._id, newNote, newNoteBadge, newNoteType);

                    // Notify player that coach added a note
                    try {

                      const { auth } = await import('../api');
                      if (window.fcmToken && auth.registerFcmToken) {
                        await auth.registerFcmToken(window.fcmToken);
                      } else {
                      }

                      // Send notification to player
                      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notes/notify-player-note`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({
                          playerId: viewingStudent._id,
                          notePreview: getPlainText(newNote).substring(0, 100)
                        })
                      });

                      const responseData = await response.json();

                      if (response.ok) {
                      } else {
                        console.warn(`⚠️ Notification failed: ${responseData.message}`);
                      }
                    } catch (notifyError) {
                      console.error('❌ Could not send notification:', notifyError.message);
                      console.error('   Stack:', notifyError.stack);
                      // Don't fail the note save if notification fails
                    }

                    // Upload photos if any
                    if (notePhotos && notePhotos.length > 0) {
                      const savedNoteId = savedNote.data?._newNoteId;
                      if (savedNoteId) {
                        for (const photoData of notePhotos) {
                          if (photoData.file) {
                            try {
                              await notes.uploadPhoto(savedNoteId, photoData.file, photoData.caption, viewingStudent._id);
                            } catch (photoErr) {
                              console.error(`❌ Error uploading photo:`, photoErr);
                            }
                          }
                        }
                      }
                    }

                    // Reset form
                    setNewNote('');
                    setNewNoteBadge(null);
                    setNewNoteType(null);
                    setNotePhotos([]);

                    // Refresh student data
                    await loadPlayers();
                    const freshPlayers = await notes.getAllPlayers();
                    const freshStudent = freshPlayers.data.find(p => p._id === viewingStudent._id);
                    if (freshStudent) {
                      setViewingStudent(freshStudent);
                    }
                    setScrollKey(prev => prev + 1);
                    showAlertModal('Success', 'Note added successfully');
                  } catch (error) {
                    console.error('Error adding note:', error);
                    showAlertModal('Error', 'Failed to add note');
                  }
                }}
                disabled={!getPlainText(newNote)}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  background: getPlainText(newNote)
                    ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
                    : '#e0e0e0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: getPlainText(newNote) ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  fontWeight: '700',
                  transition: 'all 0.3s ease',
                  boxShadow: getPlainText(newNote)
                    ? '0 4px 15px rgba(47, 75, 162, 0.4)'
                    : 'none',
                  opacity: getPlainText(newNote) ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (getPlainText(newNote)) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(47, 75, 162, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (getPlainText(newNote)) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(47, 75, 162, 0.4)';
                  }
                }}
              >
                <span style={{ fontSize: '18px' }}>📝</span>
                <span>Add Note</span>
              </button>
            </div>

            {/* Notes List (includes Pocket recordings) */}
            <div className="notes-list" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {pocketNotesLoading ? (
                <LoadingSkeleton type="card" count={2} />
              ) : viewingStudent.notes.length === 0 && pocketNotes.length === 0 ? (
                <div style={{
                  background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                  padding: '60px 40px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  color: 'white',
                  boxShadow: '0 10px 40px rgba(47, 75, 162, 0.3)'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>📭</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>
                    No notes yet
                  </div>
                  <div style={{ fontSize: '16px', opacity: 0.9 }}>
                    Add your first note above
                  </div>
                </div>
              ) : (
                (() => {
                  // Transform Pocket notes to regular note format and merge
                  const transformedPocketNotes = pocketNotes.map(pNote => ({
                    _id: pNote.id,
                    text: pNote.autoPocketSummary || 'No summary available',
                    createdAt: pNote.createdAt,
                    isPocketNote: true
                  }));

                  // Merge and sort by date
                  const allNotes = [...viewingStudent.notes, ...transformedPocketNotes]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                  return allNotes.map(note => (
                    <div
                      key={note._id}
                      data-note-id={note._id}
                      className="note-item"
                      style={{
                        // Same literal values PlayerView's own note cards use
                        // (see NotesTab.js) - the global dark-theme sweep in
                        // darkTheme.css matches these exact patterns
                        // (`background: white`, `color: #333`) and re-colors
                        // them for the dark theme too, so this card ends up
                        // identical to a player's own note card in both
                        // themes instead of hardcoding one look forever.
                        background: 'white',
                        padding: '25px',
                        borderRadius: '15px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
                        border: '2px solid rgba(47, 75, 162, 0.2)',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '15px',
                        flexWrap: 'wrap'
                      }}>
                        {note.performanceBadge && (
                          <PerformanceBadge level={note.performanceBadge} size="small" />
                        )}
                        {note.noteType && (
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            background: note.noteType === 'lesson' ? '#3498db' : '#f39c12',
                            color: 'white'
                          }}>
                            {note.noteType === 'lesson' ? '📚 Lesson' : '💡 Wisdom'}
                          </span>
                        )}
                        {note.lessonTitle && (
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            background: '#9b59b6',
                            color: 'white'
                          }}>
                            📅 {note.lessonTitle}
                          </span>
                        )}
                      </div>

                      {editingNoteId === note._id ? (
                        <div>
                          <div style={{ marginBottom: '12px' }}>
                            <RichTextEditor
                              value={editNoteText}
                              onChange={setEditNoteText}
                              maxCharacters={2000}
                              placeholder="Edit note..."
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={async () => {
                                try {
                                  await notes.updateNote(viewingStudent._id, note._id, editNoteText, note.performanceBadge, note.noteType);
                                  const response = await notes.getAllPlayers();
                                  const freshStudent = response.data.find(p => p._id === viewingStudent._id);
                                  if (freshStudent) {
                                    setViewingStudent(freshStudent);
                                  }
                                  handleCancelEdit();
                                } catch (error) {
                                  console.error('Error updating note:', error);
                                  showAlertModal('Error', 'Failed to update note');
                                }
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '8px 16px',
                                background: '#95a5a6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {note.isPocketNote || note.noteType === 'pocket_recording' ? (
                            <div style={{
                              marginBottom: '15px',
                              // RichTextDisplay inherits color (see
                              // richTextDisplay.css) - matches the
                              // written-note color below and the card's own
                              // white/#333 pairing above.
                              color: '#333'
                            }}>
                              <RichTextDisplay content={note.text} />
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
                                {(note.detectedThemes || []).map((theme, idx) => (
                                  <span key={idx} style={{
                                    display: 'inline-block',
                                    background: '#ffe0d1',
                                    color: '#d65a31',
                                    padding: '4px 10px',
                                    borderRadius: '16px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {theme}
                                  </span>
                                ))}
                                <ThemeLabelPicker
                                  coachId={user._id}
                                  existingThemes={note.detectedThemes || []}
                                  onLabel={(themeName) => handleLabelNoteTheme(note._id, themeName)}
                                />
                              </div>
                              {note.detectedThemes && note.detectedThemes.length > 0 && (
                                <ThemeSkillChips
                                  playerId={viewingStudent._id}
                                  themes={note.detectedThemes}
                                  onNavigateToSkills={() => setStudentViewTab('skills')}
                                />
                              )}
                            </div>
                          ) : (
                            <div style={{
                              fontSize: '16px',
                              lineHeight: '1.6',
                              color: '#333',
                              marginBottom: '15px',
                              wordBreak: 'break-word'
                            }}>
                              <FormattedContent content={note.text} />
                            </div>
                          )}

                          {/* Display attachments (photos) */}
                          {(() => {
                            return note.attachments && note.attachments.length > 0 && (
                              <div style={{ marginBottom: '15px' }}>
                                <AttachmentGallery
                                  attachments={note.attachments}
                                  isCoach={true}
                                  noteId={note._id}
                                />
                              </div>
                            );
                          })()}

                          <div style={{
                            fontSize: '14px',
                            color: '#7f8c8d',
                            marginBottom: '15px'
                          }}>
                            {formatDateTime(note.createdAt)}
                          </div>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              onClick={() => handleStartEdit(note)}
                              style={{
                                padding: '8px 16px',
                                background: '#3498db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this note?')) {
                                  try {
                                    await notes.deleteNote(viewingStudent._id, note._id);
                                    const response = await notes.getAllPlayers();
                                    const freshStudent = response.data.find(p => p._id === viewingStudent._id);
                                    if (freshStudent) {
                                      setViewingStudent(freshStudent);
                                    }
                                  } catch (error) {
                                    console.error('Error deleting note:', error);
                                    showAlertModal('Error', 'Failed to delete note');
                                  }
                                }
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ));
                })()
              )}
            </div>
              </>
            )}

            {/* Personal Notes View - Shared personal notes from the student */}
            {studentViewTab === 'personal' && (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '2px solid #ecf0f1',
                padding: '24px'
              }}>
                <CoachSharedPersonalNotesSection
                  playerId={viewingStudent._id}
                  playerName={viewingStudent.name}
                  currentCoachId={user._id}
                />
              </div>
            )}

            {/* Spanish (Padel Espanol) - separate from the padel skill tree */}
            {studentViewTab === 'spanish' && (
              <CoachSpanishPanel
                key={viewingStudent._id}
                playerId={typeof viewingStudent._id === 'string' ? viewingStudent._id : String(viewingStudent._id)}
                playerName={viewingStudent.name}
                reviewLogId={navTarget && navTarget.logId && navTarget.playerId === viewingStudent._id ? navTarget.logId : null}
              />
            )}

            {/* Skills View - Always mounted to preserve state */}
            {studentViewTab === 'skills' && (
              <>
                <SkillTree
                  key={viewingStudent._id}
                  playerName={viewingStudent.name}
                  playerId={typeof viewingStudent._id === 'string' ? viewingStudent._id : (viewingStudent._id && typeof viewingStudent._id === 'object' && viewingStudent._id.$oid ? viewingStudent._id.$oid : String(viewingStudent._id))}
                  isReadOnly={false}
                  defaultGender={viewingStudent.gender || 'm'}
                  defaultHandedness={viewingStudent.handedness || 'r'}
                />
              </>
            )}
          </div>
        </div>

      </>
    );
  }

  // ═══════════════════════════════════════════════════════
  // MAIN COACH VIEW - STUDENT LIST (NO WRAPPER - App.js handles it)
  // ═══════════════════════════════════════════════════════
  return (
    <>
      {/* Main Tab Selector - Horizontally Scrollable with Swipe Support */}
      <div
        ref={tabsScrollRef}
        style={{
          padding: '16px 20px',
          // 'white' (not '#ffffff') so the dark-theme sweep in
          // darkTheme.css (which only matches the literal "white" keyword,
          // not the hex/rgb() form) actually darkens this bar - otherwise
          // it stays pure white while each unselected tab's inline
          // color: '#2c3e50' still gets swept light, going washed-out.
          background: 'white',
          borderBottom: '2px solid #ecf0f1',
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          paddingRight: '8px',
          paddingLeft: '8px'
        }}
      >
        <button
          onClick={() => setCoachMainTab('students')}
          data-active={coachMainTab === 'students'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'students'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'students' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>👥</span>
          {showTabText && <span>Students</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('questions')}
          data-active={coachMainTab === 'questions'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'questions'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'questions' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>💬</span>
          {showTabText && <span>Chat</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('bookings')}
          data-active={coachMainTab === 'bookings'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'bookings'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'bookings' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>📅</span>
          {showTabText && <span>Bookings</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('tutorials')}
          data-active={coachMainTab === 'tutorials'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'tutorials'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'tutorials' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>📚</span>
          {showTabText && <span>Tutorials</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('type-waitlist')}
          data-active={coachMainTab === 'type-waitlist'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'type-waitlist'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'type-waitlist' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>📋</span>
          {showTabText && <span>Type Waitlist</span>}
        </button>

        <button
          onClick={() => setCoachMainTab('partnerships')}
          data-active={coachMainTab === 'partnerships'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'partnerships'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'partnerships' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>🤝</span>
          {showTabText && <span>Partnerships</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('padel-matches')}
          data-active={coachMainTab === 'padel-matches'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'padel-matches'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'padel-matches' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>🏆</span>
          {showTabText && <span>Padel Matches</span>}
        </button>
        <button
          onClick={() => setCoachMainTab('updates')}
          data-active={coachMainTab === 'updates'}
          style={{
            flex: '0 0 auto',
            padding: '10px 24px',
            background: coachMainTab === 'updates'
              ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)'
              : 'rgba(0, 0, 0, 0.05)',
            color: coachMainTab === 'updates' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <span style={{ fontSize: '18px' }}>📝</span>
          {showTabText && <span>Updates</span>}
        </button>


      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Students Tab Content */}
      {coachMainTab === 'students' && (
        <>
          {/* Today's Lessons */}
          <div style={{ padding: '20px', background: '#ecf0f1' }}>
            <TodayLessons
              onRefresh={(refreshFn) => { refreshCalendarRef.current = refreshFn; }}
              onLessonClick={handleLessonClick}
              lessonNotes={lessonNotes}
            />
          </div>

      {/* Students Section */}
      <div style={{ padding: '20px', paddingBottom: '100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Students ({filteredPlayers.length})</h2>
          <button 
            onClick={() => setShowAddStudent(!showAddStudent)}
            style={{
              padding: '10px 20px',
              background: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {showAddStudent ? 'Cancel' : '+ Add Student'}
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Search students by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '14px'
            }}
          />
        </div>

        {showAddStudent && (
          <div style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            position: 'relative'
          }}>
            <input
              type="text"
              placeholder="Student Email or Name"
              value={newStudentEmail}
              onChange={(e) => handleSearchPlayers(e.target.value)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
              style={{
                width: '100%',
                padding: '12px',
                marginBottom: '15px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                fontSize: '14px'
              }}
            />

            {showSuggestions && searchSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '75px',
                left: '20px',
                right: '20px',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '5px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {searchSuggestions.map(suggestion => (
                  <div
                    key={suggestion._id}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #ecf0f1',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.background = 'white'}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{suggestion.email}</div>
                    </div>
                    {suggestion.role && (
                      <div style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: '#fff',
                        background: suggestion.role === 'player' ? '#3498db' : suggestion.role === 'manager' ? '#9b59b6' : '#e74c3c',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        marginLeft: '8px',
                        whiteSpace: 'nowrap'
                      }}>
                        {suggestion.role}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => handleAddStudent(newStudentEmail)}
              style={{
                padding: '12px 20px',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                width: '100%'
              }}
            >
              Add Student
            </button>
          </div>
        )}

        {loadingPlayers ? (
          <div style={{ padding: '20px 0' }}>
            <LoadingSkeleton type="player-card" count={3} />
          </div>
        ) : filteredPlayers.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#7f8c8d',
            fontSize: '16px'
          }}>
            {players.length === 0 ? 'No students yet. Add your first student!' : 'No students found matching your search.'}
          </div>
        ) : (
          <div className="student-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {[...filteredPlayers].sort((a, b) => a.name.localeCompare(b.name)).map(player => (
              <StudentCard
                key={player._id}
                player={player}
                onView={handleViewStudent}
                onRemove={handleRemoveStudent}
                onLongTap={handleStudentLongTap}
                formatDateTime={formatDateTime}
                refreshPlayersAfterDrop={refreshPlayersAfterDrop}
              />
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {/* Questions Tab Content */}
      {coachMainTab === 'questions' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '12px' }}>
          <ChatRoom />
        </div>
      )}

      {coachMainTab === 'bookings' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <MyAvailableSlotsTab key={slotsRefreshKey} students={players} syncBufferLessons={syncBufferLessons} navTarget={navTarget} />
          <hr style={{ margin: '20px 0', borderColor: '#ecf0f1' }} />
          <BookingRequestsTab onBookingChanged={() => setSlotsRefreshKey(prev => prev + 1)} navTarget={navTarget} />
        </div>
      )}

      {/* Tutorials Tab Content */}
      {coachMainTab === 'tutorials' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <TutorialsTab user={user} />
        </div>
      )}

      {/* Type Waitlist Tab Content */}
      {coachMainTab === 'type-waitlist' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <LessonTypeWaitlistTab />
        </div>
      )}

      {/* Partnerships/Sharing Tab Content */}
      {coachMainTab === 'partnerships' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <CoachStudentSharingTab />
        </div>
      )}

      {/* Premier Padel Matches Tab Content */}
      {coachMainTab === 'padel-matches' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <PadelNewsTab />
        </div>
      )}

      {/* Player Updates Tab Content */}
      {coachMainTab === 'updates' && (
        <div style={{ height: 'calc(100% - 60px)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <CoachUpdatesOverviewTab students={players} navTarget={navTarget} />
        </div>
      )}

{/* Role Management Popup */}
{showRoleMenu && longTapStudent && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      minWidth: '300px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '10px' }}>
        Manage {longTapStudent.name}
      </h3>

      {longTapStudent.role === 'player' ? (
        <button
          onClick={handlePromoteToManager}
          disabled={isChangingRole}
          style={{
            width: '100%',
            padding: '10px',
            marginBottom: '10px',
            backgroundColor: isChangingRole ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isChangingRole ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            opacity: isChangingRole ? 0.6 : 1
          }}
        >
          {isChangingRole ? 'Promoting...' : '👑 Promote to Manager'}
        </button>
      ) : (
        <button
          onClick={handleDemoteFromManager}
          disabled={isChangingRole}
          style={{
            width: '100%',
            padding: '10px',
            marginBottom: '10px',
            backgroundColor: isChangingRole ? '#ccc' : '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isChangingRole ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '600',
            opacity: isChangingRole ? 0.6 : 1
          }}
        >
          {isChangingRole ? 'Removing...' : '❌ Remove Manager Privileges'}
        </button>
      )}

      <button
        onClick={() => {
          setShowRoleMenu(false);
          setLongTapStudentId(null);
          setLongTapStudent(null);
        }}
        disabled={isChangingRole}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: '#ddd',
          border: 'none',
          borderRadius: '4px',
          cursor: isChangingRole ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          opacity: isChangingRole ? 0.6 : 1
        }}
      >
        Cancel
      </button>
    </div>
  </div>
)}

{/* Lesson Note Modal */}
{showModal && selectedLesson && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease-out'
  }}
  onClick={closeModal}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
    >
      {/* Title */}
      <h3 style={{ 
        marginBottom: '20px',
        color: '#2c3e50',
        fontSize: '20px',
        fontWeight: '600'
      }}>
        {selectedLesson.summary || 'Lesson Note'}
      </h3>

      {/* Lesson Details */}
      <div style={{ marginBottom: '16px' }}>
        {selectedLesson.description && (
          <div style={{ 
            fontSize: '14px', 
            color: '#7f8c8d',
            marginBottom: '8px'
          }}>
            {selectedLesson.description}
          </div>
        )}
        {selectedLesson.location && (
          <div style={{ 
            fontSize: '14px', 
            color: '#7f8c8d',
            marginBottom: '8px'
          }}>
            📍 {selectedLesson.location}
          </div>
        )}
      </div>

      {/* Note Textarea */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '14px'
        }}>
          Lesson Note
        </label>
        <textarea
          key={selectedLesson?.id || 'new'}
          value={lessonNote}
          onChange={(e) => setLessonNote(e.target.value)}
          placeholder="Write your lesson note here..."
          rows="6"
          style={{
            width: '100%',
            padding: '12px',
            border: '2px solid #ecf0f1',
            borderRadius: '8px',
            fontSize: '15px',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#2f4ba2'}
          onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
        />
      </div>

      {/* Photo Upload Widget */}
      <div style={{ marginBottom: '20px' }}>
        <PhotoUploadWidget
          key={selectedLesson?.id || 'new'}
          existingPhotos={lessonNotePhotos}
          maxPhotos={10}
          onPhotosAdded={(photos) => {
            setLessonNotePhotos(photos);
          }}
          onPhotoRemoved={(photoId) => {
            setLessonNotePhotos(prev => prev.filter(p => p.id !== photoId));
          }}
        />
      </div>

      {/* Performance Badge Picker */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          fontSize: '14px',
          color: '#2c3e50'
        }}>
          Performance Badge (Optional)
        </label>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {[
            { value: null, emoji: '⚪', label: 'None', color: '#95a5a6' },
            { value: 'excellent', emoji: '⭐', label: 'Excellent', color: '#FFD700' },
            { value: 'good', emoji: '👍', label: 'Good', color: '#27ae60' },
            { value: 'average', emoji: '👌', label: 'Average', color: '#3498db' },
            { value: 'needs_work', emoji: '📈', label: 'Needs Work', color: '#e67e22' },
            { value: 'keep_trying', emoji: '💪', label: 'Keep Trying', color: '#9b59b6' }
          ].map(badge => (
            <button
              key={badge.value || 'none'}
              onClick={() => setNewNoteBadge(badge.value)}
              style={{
                flex: '1 1 calc(33.333% - 8px)',
                minWidth: '100px',
                padding: '10px 8px',
                background: newNoteBadge === badge.value ? badge.color : 'white',
                color: newNoteBadge === badge.value ? 'white' : '#2c3e50',
                border: `2px solid ${badge.color}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <span style={{ fontSize: '18px' }}>{badge.emoji}</span>
              <span>{badge.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '12px',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={closeModal}
          style={{
            padding: '10px 20px',
            background: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            await handleSaveLessonNote();
            closeModal();
          }}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Save Note
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
}

export default CoachView;